import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { animalApi, farmApi, dashboardApi, DashboardStats } from '../services/api';
import { Animal, Farm } from '../types';
import AnimalCard from '../components/AnimalCard';
import { handleApiError } from '../utils/handleApiError';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [recentAnimals, setRecentAnimals] = useState<Animal[]>([]);
  const [farms, setFarms] = useState<Farm[]>([]);
  const [showAddFarm, setShowAddFarm] = useState(false);
  const [selectedAnimalId, setSelectedAnimalId] = useState<number | null>(null);
  const [newFarm, setNewFarm] = useState<Partial<Farm>>({
    name: '',
    address: '',
    description: '',
  });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      const [dashboardStats, animalsResponse, farmsResponse] = await Promise.all([
        dashboardApi.getStats(),
        animalApi.getAll(),
        farmApi.getAll()
      ]);

      const animals: Animal[] = animalsResponse.data;
      const farms: Farm[] = farmsResponse;

      setStats(dashboardStats);
      setRecentAnimals(animals.slice(0, 10));
      setFarms(farms);
    } catch (error) {
      handleApiError(error, 'Nem sikerült betölteni az irányítópult adatait');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFarm = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await farmApi.create({ ...newFarm, company_id: user!.company_id  });
      setShowAddFarm(false);
      setNewFarm({
        name: '',
        address: '',
        description: '',
      });
      loadDashboardData();
    } catch (error) {
      handleApiError(error, 'Nem sikerült létrehozni a telephelyet');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{t('dashboard', 'menu')}</h1>
        <p className="mt-2 text-gray-600">
          {t('welcome', 'dashboard')}, {user?.name}! ({user?.role.replace('_', ' ')})
        </p>
      </div>

      {/* Active Period Banner */}
      {stats?.active_period && (
        <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-800">
                {t('active_period', 'dashboard')}: {stats.active_period.name}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {new Date(stats.active_period.start_date).toLocaleDateString('hu-HU')} - {new Date(stats.active_period.end_date).toLocaleDateString('hu-HU')}
              </p>
            </div>
            {stats.period_costs && (
              <div className="text-right">
                <p className="text-sm font-medium text-blue-800">
                  {stats.period_costs.approved.toLocaleString('hu-HU')} Ft / {stats.period_costs.total.toLocaleString('hu-HU')} Ft
                </p>
                <p className="text-xs text-blue-600">{t('approved_total_costs', 'dashboard')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Stats Cards - 6 cards in 3x2 grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {/* Active Animals */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-green-500 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{t('active_livestock', 'dashboard')}</dt>
                  <dd className="text-2xl font-bold text-gray-900">{stats?.active_animals || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Farms Count */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-yellow-500 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h1a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{t('farms', 'dashboard')}</dt>
                  <dd className="text-2xl font-bold text-gray-900">{stats?.farms_count || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Average Daily Weight Gain */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-blue-500 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{t('avg_daily_weight_gain', 'dashboard')}</dt>
                  <dd className="text-2xl font-bold text-gray-900">{stats?.avg_daily_weight_gain.toFixed(2) || 0} kg</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Total Estimated Weight */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-purple-500 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{t('estimated_total_weight', 'dashboard')}</dt>
                  <dd className="text-2xl font-bold text-gray-900">{stats?.total_estimated_weight.toLocaleString('hu-HU') || 0} kg</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Slaughter Ready */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-orange-500 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{t('slaughter_ready', 'dashboard')}</dt>
                  <dd className="text-2xl font-bold text-gray-900">{stats?.slaughter_ready_count || 0}</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        {/* Average Cost Per Animal Per Day */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-500 rounded-md flex items-center justify-center">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">{t('avg_cost_per_animal_day', 'dashboard')}</dt>
                  <dd className="text-2xl font-bold text-gray-900">{stats?.avg_cost_per_animal_per_day.toLocaleString('hu-HU') || 0} Ft</dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Alerts Panel */}
      {stats && (stats.alerts.animals_under_withdrawal > 0 || stats.alerts.low_stock_alerts > 0) && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">{t('warnings', 'dashboard')}</h3>
              <div className="mt-2 text-sm text-red-700 space-y-2">
                {stats.alerts.animals_under_withdrawal > 0 && (
                  <p>• {stats.alerts.animals_under_withdrawal} {t('animals_under_withdrawal', 'dashboard')}</p>
                )}
                {stats.alerts.low_stock_alerts > 0 && (
                  <div>
                    <p className="font-medium">• Alacsony takarmány készlet ({stats.alerts.low_stock_alerts}):</p>
                    <div className="ml-4 mt-1 space-y-1">
                      {stats.alerts.low_stock_items.map((item) => (
                        <p key={item.id} className="text-xs">
                          - {item.feed_name}: {item.current_quantity} {item.unit} (minimum: {item.minimum_alert} {item.unit})
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                {t('farm_management', 'dashboard')}
              </h3>
              <button
                onClick={() => setShowAddFarm(true)}
                className="bg-green-600 text-white px-3 py-1 rounded-md text-sm hover:bg-green-700 transition-colors"
              >
                + {t('new_farm', 'dashboard')}
              </button>
            </div>
            <div className="space-y-3">
              {farms.slice(0, 5).map((farm) => (
                <div
                  key={farm.id}
                  onClick={() => navigate('/farms')}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 rounded px-2 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-blue-600 hover:text-blue-800">
                      {farm.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {farm.address}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      {t('active', 'status')}
                    </span>
                  </div>
                </div>
              ))}
              {farms.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  {t('no_farms_yet', 'dashboard')}
                </p>
              )}
            </div>
          </div>
        </div>

      {/* Add Farm Form Modal */}
      {showAddFarm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white">
            <h3 className="text-lg font-bold text-gray-900 mb-4">{t('add_new_farm', 'dashboard')}</h3>
            <form onSubmit={handleAddFarm} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('farm_name', 'form')} *</label>
                <input
                  type="text"
                  required
                  value={newFarm.name}
                  onChange={(e) => setNewFarm({ ...newFarm, name: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  placeholder={t('farm_name_placeholder', 'form')}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">{t('address', 'form')} *</label>
                <input
                  type="text"
                  required
                  value={newFarm.address}
                  onChange={(e) => setNewFarm({ ...newFarm, address: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  placeholder={t('address_placeholder', 'form')}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">{t('description', 'form')}</label>
                <textarea
                  value={newFarm.description}
                  onChange={(e) => setNewFarm({ ...newFarm, description: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                  rows={3}
                  placeholder={t('description_placeholder', 'form')}
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddFarm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  {t('cancel', 'action')}
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  {t('add', 'action')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="mt-8 bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            {t('recent_animals', 'dashboard')}
          </h3>
            <div className="space-y-3">
              {recentAnimals.slice(0, 5).map((animal) => (
                <div
                  key={animal.id}
                  onClick={() => setSelectedAnimalId(animal.id)}
                  className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 rounded px-2 transition-colors"
                >
                  <div>
                    <p className="text-sm font-medium text-blue-600 hover:text-blue-800">
                      {t('ear_tag', 'animal')}: {animal.ear_tag}
                    </p>
                    <p className="text-xs text-gray-500">
                      {animal.breed} • {animal.gender === 'him' ? t('male', 'animal') : t('female', 'animal')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      animal.status === 'aktiv' ? 'bg-green-100 text-green-800' :
                      animal.status === 'eladott' ? 'bg-blue-100 text-blue-800' :
                      animal.status === 'levagott' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {animal.status.charAt(0).toUpperCase() + animal.status.slice(1)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

      {/* Animal Card Modal */}
      {selectedAnimalId && (
        <AnimalCard
          animalId={selectedAnimalId}
          onClose={() => setSelectedAnimalId(null)}
        />
      )}
    </div>
  );
};

export default Dashboard;